/**
 * AdminCalendarView — thin stateless wrapper around <PooCalendar> for
 * admin scope (Backlog E.4).
 *
 * Responsibility:
 *   - Build the admin-scope resolver (color-by-provider + muted
 *     cancelled rendering per Q8(b)+(c))
 *   - Render the provider legend chip strip via PooCalendar's
 *     `toolbarSlot`
 *   - Pass `showCancelled={true}` so admin sees cancelled bookings
 *     (provider/customer scope leaves the default `false`, so
 *     ProviderSchedulePage stays byte-identical)
 *
 * NOT in scope here:
 *   - Filter state (lives in AdminBookingsPage)
 *   - Date navigation cursor (controlled by the page)
 *   - Booking detail modal (rendered by the page)
 *   - Fetch (page-owned)
 *
 * Field mapping: the bookings response from /api/admin/bookings
 * already matches the shape PooCalendar consumes (it calls
 * isoToLocalDate(b.service_date, b.preferred_window.start/end)
 * internally and attaches the full booking as event.resource). No
 * transformation needed in this wrapper.
 */
import { useMemo } from "react";
import PooCalendar from "@/components/calendar/PooCalendar";
import { makeAdminResolver } from "./useAdminEventStyle";
import ProviderLegend from "./ProviderLegend";


export default function AdminCalendarView({
  events,
  mode,
  onModeChange,
  date,
  onNavigate,
  onSelectEvent,
  providers,
  testidPrefix = "admin-calendar",
}) {
  // Stable across renders — resolver only depends on the booking it's
  // called with, never on `providers`. Per useEventStyleResolver.js:13-16
  // the inline-arrow form would invalidate PooCalendar's events memo.
  const resolver = useMemo(() => makeAdminResolver(), []);

  return (
    <div data-testid="admin-calendar-view">
      <PooCalendar
        events={events}
        mode={mode}
        onModeChange={onModeChange}
        date={date}
        onNavigate={onNavigate}
        views={["month", "week", "day"]}
        eventStyleResolver={resolver}
        onSelectEvent={onSelectEvent}
        showCancelled={true}
        ariaLabel="Admin bookings calendar"
        testidPrefix={testidPrefix}
        toolbarSlot={
          <ProviderLegend providers={providers} testidPrefix={testidPrefix} />
        }
      />
    </div>
  );
}
